import useAuthStore from '@/domains/authentication/store/auth.store';
import apiSettings from '@/domains/settings/api/settings.api';
import DeviceLightSensibilityEnum from '@/domains/scan/typescript/enums/DeviceLightSensibilityEnum';
import PodonexVersionChannelEnum from '@/domains/settings/typescript/enums/PodonexVersionChannelEnum';
import { detectOS } from '@/domains/settings/utils/detectOS';
import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import type SettingsInterface from '@/domains/settings/typescript/interfaces/SettingsInterface';
import type PodonexConfigurationInterface from '@/domains/settings/typescript/interfaces/PodonexConfigurationInterface';
import type PodonexVersionDTOInterface from '@/domains/settings/typescript/interfaces/PodonexVersionDTOInterface';
import type { PodonexVersionEntryInterface } from '@/domains/settings/typescript/interfaces/PodonexVersionDTOInterface';

export default defineStore('settings', () => {
    const authStore = useAuthStore();

    const defaultManufacturePreferences: SettingsInterface = {
        '@id': '',
        id: '',
        userId: '',
        manufacture: null,
        padProvided: false,
        viewSynchronized: false,
        comment: '',
        isSanded: false,
        isGlued: false,
    };

    const manufacturePreferencesIri = ref<string>();
    const manufacturePreferences = ref<SettingsInterface>(defaultManufacturePreferences);

    const fetchManufacturePreferences = async (): Promise<void> => {
        if (manufacturePreferencesIri.value !== undefined) {
            manufacturePreferences.value = await apiSettings.getManufactureSettingsFromIri(
                manufacturePreferencesIri.value,
            );
        } else {
            manufacturePreferences.value.userId = authStore.userId ? `/users/${authStore.userId}` : '';
        }
    };

    const defaultPodonexConfiguration: PodonexConfigurationInterface = {
        '@id': '',
        id: '',
        userId: '',
        scannerId: '',
        lightSensibility: DeviceLightSensibilityEnum.NORMAL,
        sobelThreshold: 10,
        cleaningSteps: 15,
        brightness: '0',
        contrast: '0',
        negativeBrightness: '0',
        negativeContrast: '0',
        podonexVersionChannel: PodonexVersionChannelEnum.STABLE,
    };

    const podonexConfigurationIri = ref<string>();
    const podonexConfiguration = ref<PodonexConfigurationInterface>(defaultPodonexConfiguration);

    const fetchPodonexConfiguration = async (): Promise<void> => {
        if (podonexConfigurationIri.value !== undefined) {
            podonexConfiguration.value = await apiSettings.getPodonexConfigurationFromIri(
                podonexConfigurationIri.value,
            );
        } else {
            podonexConfiguration.value.userId = `/users/${authStore.userId}`;
        }
    };

    const podonexVersionData = ref<PodonexVersionDTOInterface>();
    const availableVersions = computed<PodonexVersionEntryInterface[]>(() => podonexVersionData.value?.versions ?? []);

    const fetchPodonexVersionData = async (): Promise<PodonexVersionDTOInterface | undefined> => {
        if (podonexVersionData.value !== undefined) {
            return podonexVersionData.value;
        }
        const clientOS = detectOS();
        if (clientOS === 'windows') {
            podonexVersionData.value = await apiSettings.getPodonexLatestWindowsVersion();
        } else if (clientOS === 'mac') {
            podonexVersionData.value = await apiSettings.getPodonexLatestMacOSVersion();
        }
        return podonexVersionData.value;
    };

    function $reset() {
        console.info('reset settings store');
        manufacturePreferencesIri.value = undefined;
        manufacturePreferences.value = defaultManufacturePreferences;
        podonexConfigurationIri.value = undefined;
        podonexConfiguration.value = defaultPodonexConfiguration;
        podonexVersionData.value = undefined;
    }

    return {
        manufacturePreferences,
        manufacturePreferencesIri,
        fetchManufacturePreferences,

        podonexConfiguration,
        podonexConfigurationIri,
        defaultPodonexConfiguration,
        fetchPodonexConfiguration,

        availableVersions,
        fetchPodonexVersionData,

        $reset,
    };
});
